import MonthCalendarPopover from '@/components/MonthCalendarPopover';
import { useT } from '@/i18n';
import { fromDateString, todayString } from '@/utils/datetime';
import { DateTime } from 'luxon';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface HorizontalDatePickerProps {
  /** Selected date as YYYY-MM-DD in restaurant local timezone. */
  selectedDate: string;
  onDateSelect: (date: string) => void;
  /** Optional control pinned at the trailing edge, after the calendar button
   *  (e.g. a filter button) — lets a caller merge extra actions into the bar. */
  trailing?: ReactNode;
}

// Presentation lives in `.rsv-date-strip*` classes (styles.css). These two must
// stay in sync with the CSS (`.rsv-date-strip-day` width + scroll `gap`) because
// the scroll-centering math below is computed in JS.
const ITEM_WIDTH = 48;
const ITEM_GAP = 6;
const CHUNK = 30;
const EDGE_THRESHOLD = 200;

function HorizontalDatePicker({ selectedDate, onDateSelect, trailing }: HorizontalDatePickerProps) {
  const t = useT();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const calBtnRef = useRef<HTMLButtonElement>(null);
  const baseDateRef = useRef<string>(todayString());
  const [range, setRange] = useState({ start: -CHUNK, end: CHUNK });
  const [calOpen, setCalOpen] = useState(false);
  const didInitialScrollRef = useRef(false);
  const prependCountRef = useRef(0);

  const dates: string[] = [];
  const base = fromDateString(baseDateRef.current);
  for (let i = range.start; i < range.end; i++) {
    dates.push(base.plus({ days: i }).toISODate()!);
  }

  useLayoutEffect(() => {
    if (prependCountRef.current > 0 && scrollContainerRef.current) {
      const delta = prependCountRef.current * (ITEM_WIDTH + ITEM_GAP);
      scrollContainerRef.current.scrollLeft += delta;
      prependCountRef.current = 0;
    }
  }, [range.start]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const offsetFromBase = Math.round(
      fromDateString(selectedDate).diff(fromDateString(baseDateRef.current), 'days').days,
    );
    const indexInList = offsetFromBase - range.start;
    if (indexInList < 0 || indexInList >= dates.length) return;
    const itemLeft = indexInList * (ITEM_WIDTH + ITEM_GAP);
    const scrollTo = itemLeft - container.offsetWidth / 2 + ITEM_WIDTH / 2;
    container.scrollTo({
      left: scrollTo,
      behavior: didInitialScrollRef.current ? 'smooth' : 'auto',
    });
    didInitialScrollRef.current = true;
    // Re-centre only when the selection changes - not when the rendered window
    // (range/dates) grows during infinite scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    if (scrollLeft < EDGE_THRESHOLD) {
      prependCountRef.current += CHUNK;
      setRange((r) => ({ ...r, start: r.start - CHUNK }));
    } else if (scrollWidth - scrollLeft - clientWidth < EDGE_THRESHOLD) {
      setRange((r) => ({ ...r, end: r.end + CHUNK }));
    }
  };

  const today = todayString();

  return (
    <div className="rsv-date-strip no-swipeback">
      <div className="rsv-date-strip-scroll" ref={scrollContainerRef} onScroll={handleScroll}>
        {dates.map((dateStr) => {
          const selected = dateStr === selectedDate;
          const isToday = dateStr === today;
          const dt = DateTime.fromISO(dateStr);

          return (
            <button
              key={dateStr}
              type="button"
              className={`rsv-date-strip-day no-ripple${
                selected ? ' rsv-date-strip-day--selected' : ''
              }${isToday ? ' rsv-date-strip-day--today' : ''}`}
              onClick={() => onDateSelect(dateStr)}
            >
              <span className="rsv-date-strip-day-weekday">{dt.toFormat('ccccc')}</span>
              <span className="rsv-date-strip-day-num">{dt.toFormat('d')}</span>
            </button>
          );
        })}
      </div>

      {/* Trailing button → iOS-style month calendar popover */}
      <button
        ref={calBtnRef}
        type="button"
        className="rsv-date-strip-cal-btn no-ripple"
        aria-label={t.pickDate}
        onClick={() => setCalOpen(true)}
      >
        <i className="f7-icons">calendar</i>
      </button>

      {trailing}

      <MonthCalendarPopover
        open={calOpen}
        onClose={() => setCalOpen(false)}
        anchorEl={calBtnRef.current}
        selectedDate={selectedDate}
        onSelect={onDateSelect}
      />
    </div>
  );
}

export default HorizontalDatePicker;
